import type { LandingContent } from "./types";
import ventHero1280 from "@/assets/lp/ventilation/vent-hero-1280w.webp";
import vCondensation from "@/assets/lp/ventilation/vent-card-condensation.webp";
import vHumidity from "@/assets/lp/ventilation/vent-card-humidity.webp";
import vMould from "@/assets/lp/ventilation/vent-card-mould.webp";
import vMusty from "@/assets/lp/ventilation/vent-card-musty.webp";
import vEvidence from "@/assets/lp/ventilation/vent-evidence.webp";
import vStep1 from "@/assets/lp/ventilation/vent-step-1.webp";
import vStep2 from "@/assets/lp/ventilation/vent-step-2.webp";

/**
 * Body content for the /ventilation-extraction page. Drives the shared
 * LpOrderedSections renderer (trust, testimonials, pricing, FAQ).
 *
 * Page-specific bands (inspect-first, what-we-see, ventilation options,
 * process, coordinated-measure, on-the-day, social-housing) are inlined
 * in `src/components/landing/lp-ventilation-extraction.tsx`.
 *
 * Repositioning rules: no "independent" / "no commission" language; no
 * £ savings figures; no guarantee durations; strictly non-alarmist on
 * condensation/mould, describe, don't fear-sell, don't promise a cure.
 */
export const ventilationExtractionLp: LandingContent = {
 pageKind: "ventilation",
 slug: "ventilation-extraction",
 path: "/lp/ventilation-extraction",
 title: "Ventilation & Extraction In South Wales",
 metaTitle: "Ventilation & Extraction South Wales | PAS 2030 | CRS Energy",
 metaDescription:
 "Whole-house ventilation strategy across South Wales. PAS 2030 Certified, insurance-backed guarantee, inspection-led. Reduce condensation, humidity and stale air. Free assessment.",
 defaultService: "Ventilation & Extraction",
 heroHeadline: "Ventilation & Extraction In South Wales",
 heroSubheading:
 "Reduce condensation, humidity and stale air with the right ventilation strategy across Wales and the South of England, assessed for your whole home, not just one room.",
 heroBullets: [
 "PAS 2030 Certified",
 "Whole-House Assessment",
 "Insurance-Backed Guarantee",
 "Free Assessment",
 "Inspection-Led",
 ],
 heroImage: ventHero1280,
 heroAlt: "Ventilation unit and ducting installed in a South Wales home",
 heroFloatImages: [
  { src: ventHero1280, slug: "vent-hero" },
  { src: vCondensation, slug: "vent-condensation" },
  { src: vHumidity, slug: "vent-humidity" },
  { src: vMould, slug: "vent-mould" },
  { src: vMusty, slug: "vent-musty" },
  { src: vEvidence, slug: "vent-evidence" },
  { src: vStep1, slug: "vent-step1" },
  { src: vStep2, slug: "vent-step2" },
 ],
 trustItems: [
 "PAS 2030 Certified",
 "Whole-House Assessment",
 "Insurance-Backed Guarantee",
 "Free Assessment",
 "Inspection-Led",
 ],
 problemTitle: "Why Ventilation Matters",
 problemPoints: [
 {
 title: "Better-Insulated Homes Exchange Less Air",
 body: "Insulation and draught-proofing reduce uncontrolled air movement. That is generally a good thing, but it means moisture and stale air have fewer routes out.",
 },
 {
 title: "Moisture Can Build Up Without A Path Out",
 body: "Cooking, showering, drying clothes and breathing all add moisture. Without adequate ventilation, that moisture lingers and lands on the coldest surfaces.",
 },
 {
 title: "Good Ventilation Supports Indoor Air Quality",
 body: "Continuous, controlled airflow helps manage condensation and keeps indoor air fresher. The right strategy depends on the home, not a single off-the-shelf product.",
 },
 ],
 clarityTitle: "What Good Ventilation Looks Like",
 clarityBody:
 "A whole-house strategy matched to your property: the right unit in the right location, ducting sized correctly, grilles positioned where moisture is actually generated, controls set sensibly, and airflow commissioned and documented. Coordinated with any insulation or airtightness changes so airflow and fabric work together.",
 productTitle: "Ventilation Options",
 productPoints: [
 {
 title: "Positive Input Ventilation (PIV)",
 body: "A loft or wall-mounted unit gently introduces filtered air to the home, helping to displace moist, stale air through existing leakage paths.",
 },
 {
 title: "MEV / dMEV (Continuous Extract)",
 body: "Continuous low-level extraction from wet rooms with boost on demand. Suited to homes where targeted, quiet extract is the right approach.",
 },
 {
 title: "Intelligent / Humidistat Extraction",
 body: "Humidity-sensing fans that respond to actual moisture levels in bathrooms and kitchens, rather than just running on a timer.",
 },
 {
 title: "Trickle Vents",
 body: "Background ventilation through window or wall vents, used where the strategy calls for passive make-up air alongside mechanical extract.",
 },
 ],
 processSteps: [
 {
 step: 1,
 title: "Assessment",
 description:
 "We look at the whole home: moisture sources, current ventilation, humidity readings and any airtightness changes from insulation or draught-proofing.",
 icon: "magnifier",
 },
 {
 step: 2,
 title: "Strategy",
 description:
 "A written recommendation explaining the ventilation approach for your home, why it suits the property, and how it coordinates with any insulation works.",
 icon: "file",
 },
 {
 step: 3,
 title: "Install",
 description:
 "Qualified installers fit the unit, ducting and grilles cleanly, with controls and humidistats set up to suit how the home is actually used.",
 icon: "drill",
 },
 {
 step: 4,
 title: "Verification",
 description:
 "Airflow commissioned and recorded, controls demonstrated, documentation issued and the work covered by an insurance-backed guarantee.",
 icon: "certificate",
 },
 ],
 qualificationTitle: "What Affects The Strategy",
 qualificationItems: [
 "Property size, layout and number of wet rooms",
 "Existing ventilation and how well it is working",
 "Recent insulation or draught-proofing changes",
 "Access for ducting and where the unit can sit",
 "Patterns of use (occupancy, drying, cooking, showers)",
 ],
 includedItems: [
 "Free whole-house ventilation assessment",
 "Written strategy and recommendation",
 "Manufacturer-approved unit, ducting and controls",
 "Qualified installation and commissioning",
 "Airflow verification and handover demonstration",
 "Documentation and insurance-backed guarantee",
 "Aftercare guidance",
 ],
 trustSectionTitle: "Whole-House Ventilation, Properly Commissioned",
 trustSectionBody:
 "Ventilation is not a single product bolted on a wall. We treat it as a whole-house strategy: assessed, designed, installed and verified by qualified people, and coordinated with any insulation works so airflow and fabric balance correctly.",
 benefits: [
 { title: "Whole-House Approach", description: "Assessed across the home, not just one wet room in isolation." },
 { title: "Coordinated With Insulation", description: "Airflow balanced against airtightness changes from retrofit measures." },
 { title: "Commissioned Airflow", description: "Verified on completion so the system actually performs to specification." },
 { title: "Insurance-Backed Guarantee", description: "Workmanship covered for the duration of the issued guarantee." },
 ],
  testimonialsFull: [
    {
      name: "Christina Durbin",
      date: "June 2026",
      serviceTag: "Insulation & Extractor Fans",
      source: "trustpilot",
      quote:
        "I had a positive experience with this company. The assessment process took a while but I thought that was a good thing because they were being sure that no mistakes were made. The installation was seamless as was the electrical work to fit extractor fans. I was happy with the administration team and understood what to expect.",
    },
    {
      name: "Stephen D",
      date: "October 2025",
      serviceTag: "Loft Insulation & Kitchen Fan",
      source: "google",
      quote:
        "Recently had loft insulation, kitchen fan and new radiator controls fitted. All work completed within 2 days to a high standard. Workmen were fast, efficient, polite and helpful. Similarly whenever I spoke to their office staff they were both helpful and polite. I was messaged prior to each job informing me of the name and ETA of workmen. Would not hesitate to recommend.",
    },
    {
      name: "Neil Springate",
      date: "August 2025",
      serviceTag: "Boiler, Insulation & Extraction Fans",
      source: "trustpilot",
      quote:
        "I highly recommend this company. We had a new boiler, additional loft insulation and extra extraction fans fitted under a government grant scheme. It started with a two-hour survey of the house, then the ball started rolling. Great communication from everyone concerned, the installation coordinator had everything running smoothly. Everyone turned up on time, they all worked quickly and cleanly. The whole installation could not have run more smoothly.",
    },
  ],
 faqs: [
 {
 question: "Will ventilation fix my condensation and mould?",
 answer:
 "Ventilation helps manage condensation and supports indoor air quality, but it is rarely the whole story. We assess the home first to understand the moisture sources and the fabric, and we describe what the ventilation will and will not do honestly. We do not promise to cure mould from a single product fit.",
 },
 {
 question: "PIV or extraction, which is better?",
 answer:
 "Neither is automatically better. PIV introduces filtered air to dilute and displace moist air through existing leakage paths, while continuous extract (MEV / dMEV) removes moisture at source from wet rooms. The right choice depends on your property, layout and how the home is used, which is why the assessment comes first.",
 },
 {
 question: "Are these systems noisy?",
 answer:
 "Modern PIV, MEV and humidistat extract fans are designed for continuous low-level running and are typically very quiet in normal operation. Boost modes are louder but short-lived. We size and locate units to keep noise unobtrusive.",
 },
 {
 question: "Do I need ventilation after insulation work?",
 answer:
 "Often, yes. Insulation and draught-proofing reduce uncontrolled airflow, which means the existing ventilation may no longer be enough. We frequently assess ventilation alongside or after insulation upgrades to keep airflow and airtightness balanced.",
 },
 {
 question: "What do these systems cost to run?",
 answer:
 "Continuous ventilation systems are designed to use very low power in normal operation. Exact running costs depend on the unit, settings and tariff, so we describe running cost qualitatively rather than quoting a fixed figure.",
 },
 {
 question: "Is this suitable for rented or social housing properties?",
 answer:
 "Yes. We work with landlords, managing agents and housing associations on condensation and mould response, communal ventilation, tenant guidance, reporting and completion documentation, in a non-alarmist and compliance-minded way.",
 },
 ],
 ctaTitle: "Book Your Free Ventilation Assessment",
 sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
